import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReviewSuggestion, ReviewStats, SuggestionStatus, GeneratingStep, FoundItem } from '../types/review';

// ─── Undo entry ──────────────────────────────────

interface UndoEntry {
  id: string;
  sectionRef: string;
  action: 'accepted' | 'dismissed';
}

// ─── Hook ────────────────────────────────────────

// ─── Default generating steps ─────────────────────

const DEFAULT_STEPS: GeneratingStep[] = [
  { id: 'parse', label: 'Reading document structure', status: 'pending' },
  { id: 'clauses', label: 'Identifying clauses and terms', status: 'pending' },
  { id: 'compare', label: 'Comparing against market standards', status: 'pending' },
  { id: 'draft', label: 'Drafting revision suggestions', status: 'pending' },
  { id: 'prepare', label: 'Preparing review cards', status: 'pending' },
];

export type StatusChangeCallback = (suggestionId: string, status: SuggestionStatus) => void;

export default function useReviewState() {
  /** Mutable ref — set by the consumer (App.tsx) after auth is available. */
  const onStatusChangeRef = useRef<StatusChangeCallback | null>(null);
  const [suggestions, setSuggestions] = useState<ReviewSuggestion[]>([]);
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | null>(null);
  const [documentView, setDocumentView] = useState<'original' | 'revised'>('original');
  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Backend review tracking ────────────────────────
  const [reviewId, setReviewId] = useState<string | null>(null);

  // ─── Generating phase state ───────────────────────
  const [generating, setGenerating] = useState(false);
  const [generatingSteps, setGeneratingSteps] = useState<GeneratingStep[]>([]);
  const [foundItems, setFoundItems] = useState<FoundItem[]>([]);
  const [generatingProgress, setGeneratingProgress] = useState(0);
  const [generatingError, setGeneratingError] = useState<string | null>(null);
  const generatingTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // ─── Derived stats ─────────────────────────────

  const stats: ReviewStats = useMemo(() => {
    const total = suggestions.length;
    const accepted = suggestions.filter((s) => s.status === 'accepted').length;
    const dismissed = suggestions.filter((s) => s.status === 'dismissed').length;
    const resolved = accepted + dismissed;
    const remaining = total - resolved;
    const highPriority = suggestions.filter((s) => s.priority === 'high').length;
    const rewrites = suggestions.filter((s) => s.category === 'rewrite').length;
    const additions = suggestions.filter((s) => s.category === 'addition').length;
    const deletions = suggestions.filter((s) => s.category === 'deletion').length;
    return { total, accepted, dismissed, resolved, remaining, highPriority, rewrites, additions, deletions };
  }, [suggestions]);

  // ─── Helpers ───────────────────────────────────

  const advanceToNextPending = useCallback(
    (currentId: string, updated: ReviewSuggestion[]) => {
      const currentIdx = updated.findIndex((s) => s.id === currentId);
      // Look forward for next pending
      for (let i = currentIdx + 1; i < updated.length; i++) {
        if (updated[i].status === 'pending') {
          setActiveSuggestionId(updated[i].id);
          return;
        }
      }
      // Wrap around from start
      for (let i = 0; i < currentIdx; i++) {
        if (updated[i].status === 'pending') {
          setActiveSuggestionId(updated[i].id);
          return;
        }
      }
      // No pending left
      setActiveSuggestionId(null);
    },
    [],
  );

  const showUndo = useCallback((entry: UndoEntry) => {
    // Clear any existing timer
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoEntry(entry);
    undoTimerRef.current = setTimeout(() => {
      setUndoEntry(null);
      undoTimerRef.current = null;
    }, 4000);
  }, []);

  // ─── Actions ───────────────────────────────────

  const acceptSuggestion = useCallback(
    (id: string) => {
      setSuggestions((prev) => {
        const next = prev.map((s) => (s.id === id ? { ...s, status: 'accepted' as const } : s));
        advanceToNextPending(id, next);
        return next;
      });
      const s = suggestions.find((s) => s.id === id);
      if (s) showUndo({ id, sectionRef: s.sectionRef, action: 'accepted' });
      onStatusChangeRef.current?.(id, 'accepted');
    },
    [suggestions, advanceToNextPending, showUndo],
  );

  const dismissSuggestion = useCallback(
    (id: string) => {
      setSuggestions((prev) => {
        const next = prev.map((s) => (s.id === id ? { ...s, status: 'dismissed' as const } : s));
        advanceToNextPending(id, next);
        return next;
      });
      const s = suggestions.find((s) => s.id === id);
      if (s) showUndo({ id, sectionRef: s.sectionRef, action: 'dismissed' });
      onStatusChangeRef.current?.(id, 'dismissed');
    },
    [suggestions, advanceToNextPending, showUndo],
  );

  const undoLast = useCallback(() => {
    if (!undoEntry) return;
    setSuggestions((prev) =>
      prev.map((s) => (s.id === undoEntry.id ? { ...s, status: 'pending' as SuggestionStatus } : s)),
    );
    setActiveSuggestionId(undoEntry.id);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    onStatusChangeRef.current?.(undoEntry.id, 'pending');
    setUndoEntry(null);
  }, [undoEntry]);

  const acceptAllRemaining = useCallback(() => {
    setSuggestions((prev) => {
      const pending = prev.filter((s) => s.status === 'pending');
      for (const s of pending) onStatusChangeRef.current?.(s.id, 'accepted');
      return prev.map((s) => (s.status === 'pending' ? { ...s, status: 'accepted' as const } : s));
    });
    setActiveSuggestionId(null);
  }, []);

  const dismissAllRemaining = useCallback(() => {
    setSuggestions((prev) => {
      const pending = prev.filter((s) => s.status === 'pending');
      for (const s of pending) onStatusChangeRef.current?.(s.id, 'dismissed');
      return prev.map((s) => (s.status === 'pending' ? { ...s, status: 'dismissed' as const } : s));
    });
    setActiveSuggestionId(null);
  }, []);

  const markSuggestionsAppliedToDocument = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    if (idSet.size === 0) return;
    setSuggestions((prev) => prev.map((s) => (
      idSet.has(s.id)
        ? { ...s, appliedToDocument: true, applyFailed: null }
        : s
    )));
  }, []);

  const markSuggestionApplyFailed = useCallback((id: string, reason: string) => {
    setSuggestions((prev) => prev.map((s) => (
      s.id === id
        ? { ...s, appliedToDocument: false, applyFailed: reason }
        : s
    )));
    setActiveSuggestionId(id);
  }, []);

  const loadSuggestions = useCallback((data: ReviewSuggestion[]) => {
    setSuggestions(data);
    // Set first pending as active
    const firstPending = data.find((s) => s.status === 'pending');
    setActiveSuggestionId(firstPending?.id ?? null);
    setDocumentView('original');
    setUndoEntry(null);
  }, []);

  // ─── Generating phase actions ─────────────────────

  const clearGeneratingTimers = useCallback(() => {
    generatingTimersRef.current.forEach(clearTimeout);
    generatingTimersRef.current = [];
  }, []);

  const startGenerating = useCallback(
    (pendingSuggestions: ReviewSuggestion[], onComplete: () => void) => {
      clearGeneratingTimers();
      setGenerating(true);
      setGeneratingError(null);
      setGeneratingSteps(DEFAULT_STEPS.map((s) => ({ ...s, status: 'pending' as const })));
      setFoundItems([]);
      setGeneratingProgress(0);

      // Simulate step progression
      const stepDelay = 1200; // ms per step
      const totalSteps = DEFAULT_STEPS.length;

      DEFAULT_STEPS.forEach((_step, i) => {
        // Mark step as active
        const t1 = setTimeout(() => {
          setGeneratingSteps((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, status: 'active' as const } : idx < i ? { ...s, status: 'completed' as const } : s,
            ),
          );
          setGeneratingProgress(((i + 0.5) / totalSteps) * 100);
        }, i * stepDelay);
        generatingTimersRef.current.push(t1);

        // Mark step as completed (halfway through next step's time)
        if (i < totalSteps - 1) {
          const t2 = setTimeout(() => {
            setGeneratingSteps((prev) =>
              prev.map((s, idx) => (idx === i ? { ...s, status: 'completed' as const } : s)),
            );
            setGeneratingProgress(((i + 1) / totalSteps) * 100);
          }, (i + 1) * stepDelay);
          generatingTimersRef.current.push(t2);
        }
      });

      // Simulate found items appearing progressively
      const foundItemsData: FoundItem[] = pendingSuggestions.slice(0, 3).map((s) => ({
        id: s.id,
        sectionRef: s.sectionRef,
        title: s.sectionTitle,
        description: s.shortLabel,
      }));

      foundItemsData.forEach((item, i) => {
        const t = setTimeout(() => {
          setFoundItems((prev) => [...prev, item]);
        }, 1800 + i * 800);
        generatingTimersRef.current.push(t);
      });

      // Add scanning placeholder after items
      const scanT = setTimeout(() => {
        setFoundItems((prev) => [
          ...prev,
          { id: 'scanning', sectionRef: '', title: 'Scanning remaining sections...', scanning: true },
        ]);
      }, 1800 + foundItemsData.length * 800);
      generatingTimersRef.current.push(scanT);

      // Complete after all steps
      const completeT = setTimeout(() => {
        setGeneratingSteps((prev) => prev.map((s) => ({ ...s, status: 'completed' as const })));
        setGeneratingProgress(100);
        // Brief pause then transition
        const finishT = setTimeout(() => {
          setGenerating(false);
          setGeneratingSteps([]);
          setFoundItems([]);
          setGeneratingProgress(0);
          onComplete();
        }, 600);
        generatingTimersRef.current.push(finishT);
      }, totalSteps * stepDelay);
      generatingTimersRef.current.push(completeT);
    },
    [clearGeneratingTimers],
  );

  const triggerGeneratingError = useCallback(
    (errorMessage: string, failedStepId: string) => {
      clearGeneratingTimers();
      setGeneratingSteps((prev) =>
        prev.map((s) => {
          if (s.id === failedStepId) return { ...s, status: 'error' as const };
          // Steps after the failed one stay pending
          const failedIdx = prev.findIndex((p) => p.id === failedStepId);
          const stepIdx = prev.findIndex((p) => p.id === s.id);
          if (stepIdx > failedIdx) return { ...s, status: 'pending' as const };
          // Steps before stay completed
          return s;
        }),
      );
      setGeneratingError(errorMessage);
      setFoundItems([]);
    },
    [clearGeneratingTimers],
  );

  const reset = useCallback(() => {
    setSuggestions([]);
    setActiveSuggestionId(null);
    setDocumentView('original');
    setUndoEntry(null);
    setReviewId(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    clearGeneratingTimers();
    setGenerating(false);
    setGeneratingSteps([]);
    setFoundItems([]);
    setGeneratingProgress(0);
    setGeneratingError(null);
  }, [clearGeneratingTimers]);

  return {
    suggestions,
    activeSuggestionId,
    documentView,
    undoEntry,
    stats,
    reviewId,
    setReviewId,
    onStatusChangeRef,
    generating,
    generatingSteps,
    foundItems,
    generatingProgress,
    generatingError,
    setActiveSuggestionId,
    setDocumentView,
    acceptSuggestion,
    dismissSuggestion,
    undoLast,
    acceptAllRemaining,
    dismissAllRemaining,
    markSuggestionsAppliedToDocument,
    markSuggestionApplyFailed,
    loadSuggestions,
    startGenerating,
    triggerGeneratingError,
    reset,
  };
}
